/* eslint-disable @typescript-eslint/no-use-before-define */
import { ChartConfig, Datum, SeriesData } from '../types'
import { isValidNumber } from '../charts/share/utils'

export const MAX_PROGRESS = 100
export const MAX_BAR_LENGTH = 234
export const BREAK_BAR_LEGNTH = [
  170 + 1.0 * 64,
  170 + 0.8 * 64,
  170 + 0.6 * 64,
  170 + 0.4 * 64,
  170 + 0.2 * 64
]
export const MAX_NON_BREAK_BAR_LENGTH = 130
export const MAX_NON_BREAK_PROGRESS = (MAX_NON_BREAK_BAR_LENGTH / MAX_BAR_LENGTH) * MAX_PROGRESS

type ProgressMap = Record<string, number>

const getCalcBreakProgress = (
  chartConfig: ChartConfig<'RoundBar'>,
  isDisplayBreakSymbol: (value: Datum['value']) => boolean
) => {
  const progressMap = buildProgressMap(chartConfig, isDisplayBreakSymbol)

  return (seriesData: SeriesData): number => {
    const key = seriesData.label
    return key ? progressMap[key] : 0
  }
}

export default getCalcBreakProgress

const buildProgressMap = (
  chartConfig: ChartConfig<'RoundBar'>,
  isDisplayBreakSymbol: (value: Datum['value']) => boolean
): ProgressMap => {
  const seriesData = [...chartConfig.seriesData].filter(
    (d) => !!d.label && isValidNumber(retrieveValue(d))
  )

  const { dataWithoutBreak, dataWithBreak } = partition(seriesData, isDisplayBreakSymbol)
  const breakProgress = calcBreakProgress(dataWithBreak)
  const hasBreak = dataWithBreak.length > 0
  const nonBreakProgress = calcNonBreakProgress(dataWithoutBreak, hasBreak)
  return {
    ...breakProgress,
    ...nonBreakProgress
  }
}

const retrieveValue = (seriesData: SeriesData) => seriesData.data[0].value

const partition = (data: SeriesData[], isDisplayBreakSymbol: (value: Datum['value']) => boolean) =>
  data.reduce(
    (clusters, seriesData) => {
      const value = retrieveValue(seriesData)
      if (isDisplayBreakSymbol(value)) {
        clusters.dataWithBreak.push(seriesData)
      } else {
        clusters.dataWithoutBreak.push(seriesData)
      }
      return clusters
    },
    {
      dataWithBreak: [] as SeriesData[],
      dataWithoutBreak: [] as SeriesData[]
    }
  )

const calcBreakProgress = (data: SeriesData[]): ProgressMap =>
  data.sort(descending).reduce(
    (progressMap, d, i) => ({
      ...progressMap,
      // TODO: should remove non-null assertion
      [d.label!]: (BREAK_BAR_LEGNTH[i] / MAX_BAR_LENGTH) * MAX_PROGRESS
    }),
    {}
  )

const descending = (a: SeriesData, b: SeriesData) => {
  // TODO: should remove number assertion
  const value1 = retrieveValue(a) as number
  const value2 = retrieveValue(b) as number
  return value2 - value1
}

const calcNonBreakProgress = (data: SeriesData[], hasBreak: boolean): ProgressMap => {
  const values = data.map(retrieveValue).filter(isValidNumber)
  const maxValue = Math.max(...values)

  return data.reduce((progressMap, d) => {
    // TODO: should remove as number
    const value = retrieveValue(d) as number
    return {
      ...progressMap,
      // TODO: should remove non-null assertion
      [d.label!]: (value / (maxValue || 1)) * getMaxProgress(hasBreak)
    }
  }, {})
}

const getMaxProgress = (isExceedThreshold: boolean) =>
  isExceedThreshold ? MAX_NON_BREAK_PROGRESS : MAX_PROGRESS
